/**
 * Hook and utilities for accessing labs configuration
 */

import { useMemo } from "react";
import { LabConfig } from "../types/lab.types";
import { useAuth } from "./useAuth";

/**
 * Hook to get enabled labs from configuration,
 * filtered by user's access claims when require_claim is set.
 */
export function useLabsConfig(): LabConfig[] {
  const { user } = useAuth();

  return useMemo(() => {
    const labs = window.APP_CONFIG?.labs || [];
    const claimNamespace = window.APP_CONFIG?.labsAccessClaimNamespace;
    const userClaims: string[] =
      claimNamespace && user ? (user[claimNamespace] as string[]) || [] : [];

    return labs.filter((lab) => {
      if (lab.enabled === false) return false;
      if (!lab.require_claim) return true;
      return userClaims.includes(lab.require_claim);
    });
  }, [user]);
}

export interface LabWithAccess {
  lab: LabConfig;
  hasAccess: boolean;
}

/**
 * Hook to get visible labs with access metadata.
 * Labs with access_denied: "disabled" that fail the claim check are included but flagged.
 * Labs with access_denied: "hidden" (or unset) that fail the claim check are filtered out.
 */
export function useLabsWithAccess(): LabWithAccess[] {
  const { user } = useAuth();

  return useMemo(() => {
    const labs = window.APP_CONFIG?.labs || [];
    const claimNamespace = window.APP_CONFIG?.labsAccessClaimNamespace;
    const userClaims: string[] =
      claimNamespace && user ? (user[claimNamespace] as string[]) || [] : [];

    const result: LabWithAccess[] = [];
    for (const lab of labs) {
      if (lab.enabled === false) continue;
      if (!lab.require_claim) {
        result.push({ lab, hasAccess: true });
        continue;
      }
      const hasAccess = userClaims.includes(lab.require_claim);
      if (hasAccess) {
        result.push({ lab, hasAccess: true });
      } else if (lab.access_denied === "disabled") {
        result.push({ lab, hasAccess: false });
      }
      // else: hidden (default) — filter out
    }
    return result;
  }, [user]);
}

/**
 * Hook to get labs that have federated cards.
 * Cards are decorative and shown regardless of user access.
 * Only filters by enabled status and cardUrl presence.
 */
export function useLabCards(): LabConfig[] {
  return useMemo(() => {
    const labs = window.APP_CONFIG?.labs || [];
    return labs.filter((lab) => {
      if (lab.enabled === false) return false;
      if (!lab.cardUrl) return false;
      return true;
    });
  }, []);
}

/**
 * Resolve lab URL - prefix relative URLs with base URL
 */
export function resolveLabUrl(url: string | undefined): string {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  const baseUrl = window.APP_CONFIG?.labsBaseUrl || "";
  return `${baseUrl}${url}`;
}
